import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { Icon, PlainButton, tokens, useIsMobile } from 'components/v3';
import { FONT_SYSTEM } from 'components/v3/tokens';
import {
  CARD_WIDTH,
  FOLLOW_MS,
  MOBILE_MAX,
  ORANGE,
  TOUR_PAD,
  Z,
} from './constants';
import { holeRect, padRect, placeTourCard, unionRect } from './placement';
import { counterLabel, progressPercent } from './tourModel';
import {
  endTour,
  isActiveHost,
  nextStep,
  prevStep,
  useTourState,
} from './tourStore';

// The product tour on screen (Implementation List §4, design GuideTour):
// the page dims, a spotlight with a pulsing orange ring frames the step's
// target and glides to the next one, and the step card sits beside it with
// an arrow (pinned to the bottom on phones). Clicks on the page underneath
// are blocked. Keys: → or Enter next, ← back, Esc ends.
// Only the newest FormsHelp host draws it.

const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(250, 70, 22, 0.5); }
  70% { box-shadow: 0 0 0 12px rgba(250, 70, 22, 0); }
  100% { box-shadow: 0 0 0 0 rgba(250, 70, 22, 0); }
`;

const Root = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${Z.tour};
  font-family: ${FONT_SYSTEM};
  line-height: 1.5;
`;

const Block = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Dim = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(6, 6, 16, 0.72);
  pointer-events: none;
`;

const Hole = styled.div`
  position: fixed;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(6, 6, 16, 0.72);
  pointer-events: none;
  transition: left 340ms ${EASE}, top 340ms ${EASE}, width 340ms ${EASE},
    height 340ms ${EASE};

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    bottom: -2px;
    left: -2px;
    border-radius: 14px;
    border: 2px solid ${ORANGE};
    animation: ${pulse} 1.8s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      animation: none;
    }
  }
`;

const Card = styled.div`
  position: fixed;
  width: ${CARD_WIDTH}px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  background: ${({ $dark }) => ($dark ? '#1A1A2E' : '#fff')};
  border: 1px solid
    ${({ $dark }) =>
      $dark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(15, 23, 42, 0.12)'};
  border-radius: 16px;
  box-shadow: ${({ $dark }) =>
    $dark
      ? '0 18px 48px rgba(0, 0, 0, 0.5)'
      : '0 18px 48px rgba(23, 37, 69, 0.22)'};
  text-align: left;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: ${({ $in }) => ($in ? 'none' : 'translateY(4px)')};
  transition: ${({ $in }) =>
    $in
      ? `opacity 220ms ease 80ms, transform 220ms ease 80ms, left 340ms ${EASE}, top 340ms ${EASE}`
      : 'opacity 180ms ease, transform 180ms ease'};

  ${({ $mobile }) =>
    $mobile &&
    css`
      left: 12px;
      right: 12px;
      width: auto;
      max-width: none;
      bottom: calc(12px + env(safe-area-inset-bottom));
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ARROW = {
  bottom:
    'top: -6.5px; margin-left: -6px; border-bottom-color: transparent; border-right-color: transparent;',
  top: 'bottom: -6.5px; margin-left: -6px; border-top-color: transparent; border-left-color: transparent;',
  right:
    'left: -6.5px; margin-top: -6px; border-top-color: transparent; border-right-color: transparent;',
  left: 'right: -6.5px; margin-top: -6px; border-bottom-color: transparent; border-left-color: transparent;',
};

const CardArrow = styled.span`
  position: absolute;
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  background: inherit;
  border: inherit;
  transform: rotate(45deg);
  ${({ $side }) => ARROW[$side] || ''}
`;

const Bar = styled.div`
  position: relative;
  height: 3px;
  margin: -1px -1px 0;
  border-radius: 16px 16px 0 0;
  background: ${({ $dark }) =>
    $dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
  overflow: hidden;
`;

const BarFill = styled.i`
  display: block;
  height: 100%;
  background: ${ORANGE};
  transition: width 300ms ${EASE};
`;

const Inner = styled.div`
  padding: 15px 18px 14px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${({ $dark }) => ($dark ? ORANGE : '#D63A0F')};
`;

const Chapter = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
`;

const Counter = styled.span`
  color: ${({ $t }) => $t.faint};
  letter-spacing: 0.6px;
  white-space: nowrap;
`;

const StepTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.25;
  color: ${({ $t }) => $t.text};
`;

const StepBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ $t }) => $t.sub};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const Skip = styled(PlainButton)`
  margin-right: auto;
  padding: 6px 2px;
  color: ${({ $t }) => $t.faint};
  font-size: 13px;
  font-weight: 600;

  &:hover {
    color: ${({ $t }) => $t.text};
  }
`;

const Keys = styled.span`
  display: flex;
  gap: 3px;
  margin-right: 4px;

  kbd {
    min-width: 20px;
    height: 20px;
    box-sizing: border-box;
    padding: 0 4px;
    border-radius: 5px;
    border: 1px solid ${({ $t }) => $t.fieldBorder};
    color: ${({ $t }) => $t.faint};
    font: 700 11px/18px ${FONT_SYSTEM};
    text-align: center;
  }
`;

const StepButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
  }
`;

const Ghost = styled(StepButton)`
  border: 1px solid ${({ $t }) => $t.border};
  color: ${({ $t }) => $t.text};
  font-weight: 600;
`;

const Primary = styled(StepButton)`
  background: ${ORANGE};
  color: #fff;
`;

const sameRect = (a, b) =>
  a === b ||
  (!!a &&
    !!b &&
    Math.abs(a.left - b.left) <= 0.5 &&
    Math.abs(a.top - b.top) <= 0.5 &&
    Math.abs(a.width - b.width) <= 0.5 &&
    Math.abs(a.height - b.height) <= 0.5);

const samePos = (a, b) =>
  a.left === b.left &&
  a.top === b.top &&
  a.side === b.side &&
  a.arrow === b.arrow;

const OFFSCREEN = { left: -9999, top: -9999, side: 'none', arrow: 0 };

const swallow = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

// Pointer events the click blocker swallows natively, so they never reach
// document-level listeners (React 16 itself listens on the document).
const BLOCKED = [
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'touchstart',
  'touchend',
  'contextmenu',
];

const TourOverlay = ({ hostId }) => {
  const tour = useTourState();
  const theme = useTheme();
  const t = tokens(theme);
  const dark = !!theme && theme.name === 'dark';
  const isMobile = useIsMobile(MOBILE_MAX + 1);
  const [spot, setSpot] = useState(null);
  const [pos, setPos] = useState(OFFSCREEN);
  const cardRef = useRef(null);
  const blockRef = useRef(null);
  const primaryRef = useRef(null);
  const active = tour.running && isActiveHost(hostId);
  const { ready, targets, index, steps } = tour;

  // Frame the targets and follow them if the layout moves. Between steps
  // the old frame stays put, so the spotlight glides to the next target.
  useEffect(() => {
    if (!active) {
      setSpot(null);
      setPos(OFFSCREEN);
      return undefined;
    }
    if (!ready || !targets.length) return undefined;
    const measure = () => {
      const rects = targets
        .filter((el) => el.isConnected)
        .map((el) => el.getBoundingClientRect());
      const union = unionRect(rects);
      const next = union ? padRect(union, TOUR_PAD) : null;
      setSpot((prev) => (sameRect(prev, next) ? prev : next));
    };
    measure();
    const timer = setInterval(measure, FOLLOW_MS);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, ready, targets]);

  // Keys, in the capture phase so the page underneath doesn't react too.
  // Enter on one of the card's own buttons still presses that button.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        swallow(e);
        endTour();
      } else if (e.key === 'ArrowRight') {
        swallow(e);
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        swallow(e);
        prevStep();
      } else if (e.key === 'Enter') {
        const onCard =
          e.target && e.target.closest && e.target.closest('[data-tour-card]');
        if (onCard) return;
        swallow(e);
        nextStep();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active]);

  const step = active ? steps[index] : null;

  useEffect(() => {
    const el = blockRef.current;
    if (!el) return undefined;
    BLOCKED.forEach((type) => el.addEventListener(type, swallow));
    return () =>
      BLOCKED.forEach((type) => el.removeEventListener(type, swallow));
  }, [active, step]);

  // Keyboard focus sits on Next|Done, so Enter, Tab and screen readers start
  // from the card. A dialog the tour opened may grab focus while a step
  // sets up; take it back once the step is ready.
  useEffect(() => {
    const button = primaryRef.current;
    const tourCard = cardRef.current;
    if (!active || !ready || !button || !tourCard) return;
    if (!tourCard.contains(document.activeElement)) button.focus();
  }, [active, ready, index]);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!active || !card || !step) return;
    const next = placeTourCard({
      spot,
      width: card.offsetWidth,
      height: card.offsetHeight,
      vw: window.innerWidth,
      vh: window.innerHeight,
      side: step.side || null,
    });
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, [active, spot, step, ready, isMobile]);

  if (!active || !step) return null;

  const last = index === steps.length - 1;
  const hole = spot
    ? holeRect(spot, window.innerWidth, window.innerHeight)
    : null;
  const showArrow = !isMobile && pos.side !== 'none';
  const along = pos.side === 'top' || pos.side === 'bottom';

  return ReactDOM.createPortal(
    <Root role="dialog" aria-modal="true" aria-label="Product tour">
      <Block ref={blockRef} data-testid="tour-block" />
      {hole ? <Hole data-testid="tour-hole" style={hole} /> : <Dim />}
      <Card
        ref={cardRef}
        data-tour-card=""
        data-side={isMobile ? 'none' : pos.side}
        $dark={dark}
        $in={ready}
        $mobile={isMobile}
        style={isMobile ? undefined : { left: pos.left, top: pos.top }}
      >
        {showArrow ? (
          <CardArrow
            $side={pos.side}
            style={along ? { left: pos.arrow } : { top: pos.arrow }}
          />
        ) : null}
        <Bar $dark={dark}>
          <BarFill style={{ width: `${progressPercent(steps, index)}%` }} />
        </Bar>
        <Inner aria-live="polite">
          <Eyebrow $dark={dark}>
            <Chapter>
              <Icon name={step.chapter.icon} size={13} />
              {step.chapter.label}
            </Chapter>
            <Counter $t={t}>
              {counterLabel(steps, index, tour.chapterId)}
            </Counter>
          </Eyebrow>
          <StepTitle $t={t}>{step.title}</StepTitle>
          <StepBody $t={t}>{step.body}</StepBody>
          <Foot>
            <Skip $t={t} onClick={endTour}>
              {last ? 'Close' : 'End tour'}
            </Skip>
            {isMobile ? null : (
              <Keys $t={t} aria-hidden="true">
                <kbd>←</kbd>
                <kbd>→</kbd>
              </Keys>
            )}
            {index > 0 ? (
              <Ghost $t={t} onClick={prevStep}>
                Back
              </Ghost>
            ) : null}
            <Primary ref={primaryRef} onClick={nextStep} disabled={!ready}>
              {last ? 'Done' : 'Next'}
              {last ? null : <Icon name="arrow-right" size={14} />}
            </Primary>
          </Foot>
        </Inner>
      </Card>
    </Root>,
    document.body
  );
};

TourOverlay.propTypes = {
  hostId: PropTypes.string.isRequired,
};

export default TourOverlay;
